/**
 * Intelligent feedback analysis for flute learning
 * Detects common playing errors and provides actionable guidance
 */

import { noteToMidi } from "./transposition";
import type { PitchDetectionResult } from "./types";

export type ErrorType = "correct" | "close" | "wrong-octave" | "wrong-note";

export interface NoteError {
    type: ErrorType;
    detectedNote: string;
    targetNote: string;
    feedback: string;
    octaveDifference?: number;
    semitonesDifference?: number;
}

/**
 * Analyze the error between detected note and target note
 * Provides intelligent feedback based on the type of mistake
 */
export function analyzeNoteError(
    detectedNote: string,
    targetNote: string,
    pitchData: PitchDetectionResult,
    toleranceCents: number = 50
): NoteError {
    // Convert both notes to MIDI for comparison
    const detectedMidi = noteToMidi(detectedNote);
    const targetMidi = noteToMidi(targetNote);
    const semitonesDiff = detectedMidi - targetMidi;

    // Exact match
    if (detectedNote === targetNote) {
        const centsOff = Math.abs(pitchData.centsOff || 0);

        if (centsOff < 10) {
            return {
                type: "correct",
                detectedNote,
                targetNote,
                feedback: "Perfect!",
            };
        } else if (centsOff <= toleranceCents) {
            return {
                type: "close",
                detectedNote,
                targetNote,
                feedback: "Close - adjust tuning",
            };
        }
    }

    // Check for octave errors (same note class, different octave)
    const octaveDiff = getOctaveDifference(detectedMidi, targetMidi);

    if (octaveDiff !== 0 && Math.abs(semitonesDiff) % 12 === 0) {
        // It's an octave error
        return {
            type: "wrong-octave",
            detectedNote,
            targetNote,
            feedback: generateOctaveFeedback(octaveDiff),
            octaveDifference: octaveDiff,
            semitonesDifference: semitonesDiff,
        };
    }

    // Different note entirely
    return {
        type: "wrong-note",
        detectedNote,
        targetNote,
        feedback: `Wrong note: ${detectedNote}`,
        semitonesDifference: semitonesDiff,
    };
}

/**
 * Calculate octave difference between two MIDI notes
 * Returns positive if detected is higher, negative if lower
 */
export function getOctaveDifference(detectedMidi: number, targetMidi: number): number {
    const semitonesDiff = detectedMidi - targetMidi;
    return Math.floor(semitonesDiff / 12);
}

/**
 * Get a randomized baby emoji with different skin tones for inclusive representation
 */
function getRandomBabyEmoji(): string {
    const babyEmojis = ["👶", "👶🏻", "👶🏼", "👶🏽", "👶🏾", "👶🏿"];
    return babyEmojis[Math.floor(Math.random() * babyEmojis.length)];
}

/**
 * Generate helpful feedback for octave errors
 */
export function generateOctaveFeedback(octaveDiff: number): string {
    if (octaveDiff > 0) {
        // Playing too high
        const babyEmoji = getRandomBabyEmoji();
        if (octaveDiff === 1) {
            return `${babyEmoji}💨 Blow softer!`;
        } else {
            return `${babyEmoji}💨 blow much softer`;
        }
    } else if (octaveDiff < 0) {
        // Playing too low
        if (octaveDiff === -1) {
            return `🐺💨💨 Blow harder!`;
        } else {
            return `🐺💨💨💨 blow much harder`;
        }
    }

    return "Check your octave";
}
